import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useSpring,
  animated
} from 'react-spring';

import { IconButton } from '@brandwatch/axiom-components';

import ViziaComponentHeader from './Header';
import ViziaComponentIframe from './Iframe';
import useRect from '../../hooks/useRect';

import './ViziaComponent.css';

const noop = () => {};

const interpolateOffset = (value) => `translateY(${value}px)`;

// Sets the layer index for the component, based on whether it is currently focussed
const interpolateZIndex = (value) => value === 0 ? 0 : 2;

const ViziaComponent = (props) => {
  const {
    id = null,
    show_header = true,
    uiMode = null,
    setComponentFocus = noop,
    isFocussed = false,
    loadIframes = false
  } = props;

  const cardNode = useRef(null);
  const [cardRect, onCardRectNode] = useRect();

  const [springProps, setSpringProps] = useSpring(() => ({
    offset: 0,
    config: { mass: 1, tension: 800, friction: 60 }
  }));

  const onCardNode = useCallback((node) => {
    cardNode.current = node;
    onCardRectNode(node);
  }, [onCardRectNode]);

  const focusComponent = useCallback(() => {
    if (uiMode === 'scroll') {
      setComponentFocus(id);
    }
  }, [id, setComponentFocus, uiMode]);

  const blurComponent = useCallback(() => {
    if (uiMode === 'scroll') {
      setComponentFocus(null);
    }
  }, [setComponentFocus, uiMode]);

  const {height: cardHeight} = cardRect;

  const isLifted = uiMode === 'scroll' && isFocussed;
  const isCoverEnabled = uiMode === 'scroll' && !isFocussed;

  useEffect(() => {
    if (!cardNode.current || uiMode !== 'scroll') {
      return;
    }

    if (!isFocussed) {
      setSpringProps({offset: 0});
      return;
    }

    const {top: cardRelativeTop} = cardNode.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const delta = Math.abs(viewportHeight - cardHeight);
    const offset = (delta / 2) - cardRelativeTop;

    setSpringProps({offset});
  }, [isFocussed, cardHeight, uiMode]);

  return (
    <animated.div
      className={classNames('ViziaComponent', {
        [`ViziaComponent--${uiMode}`]: true,
        'ViziaComponent--isFocussed': isFocussed
      })}
      style={{
        zIndex: springProps.offset.interpolate(interpolateZIndex)
      }}
    >
      <animated.div
        className="ViziaComponent__wrapper"
        style={{
          transform: springProps.offset.interpolate(interpolateOffset)
        }}
      >
        <div
          className="ViziaComponent__card"
          onClick={focusComponent}
          ref={onCardNode}
        >
          {show_header && (
            <ViziaComponentHeader {...props} />
          )}
          <div className="ViziaComponent__body">
            <ViziaComponentIframe
              {...props}
              shouldLoadIframe={loadIframes}
            />
            <div
              className={classNames('ViziaComponent__cover', {
                'ViziaComponent__cover--enabled': isCoverEnabled
              })}
            />
          </div>
        </div>
        <div className='ViziaComponent__focusContext'>
          <IconButton
            name="cross"
            size="small"
            buttonStyle="secondary"
          />
        </div>
      </animated.div>
      <div
        className="ViziaComponent__focusMask"
        onClick={blurComponent}
      />
    </animated.div>
  );
};

ViziaComponent.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  show_header: PropTypes.bool,
  uiMode: PropTypes.string,
  setComponentFocus: PropTypes.func,
  isFocussed: PropTypes.bool,
  loadIframes: PropTypes.bool
};

export default ViziaComponent;
